import { api } from "./api.js";
import type { CardOrderBilling, OrdersRemoved } from "./cardOrders";

/**
 * Logo orders (Sales > Logo files): what the logo studio on the platform's own sites sold,
 * with the buyer, the logo, the invoice and the ZIP of its files. The orders live in the
 * platform database, not the CRM's; the invoice is the CRM's own
 * (`apps/api/src/modules/logo-orders/routes/crm.ts`).
 */

/** Who the invoice is made out to: the same block as a card order's. */
export type LogoOrderBilling = CardOrderBilling;

export interface LogoOrderRow {
  id: string;
  code: string;
  /** pending | paid */
  status: string;
  email: string;
  locale: string;
  /** The company name on the logo, as the buyer typed it. */
  name: string;
  amountCents: number;
  currency: string;
  billing: LogoOrderBilling | null;
  invoiceId: string;
  invoiceNumber: string;
  /** Made with test keys: its payment page was a test one (a paid one's invoice is numbered outside the sequence). */
  test: boolean;
  /** The desk may delete it: a test order, or an unpaid one that can no longer be paid. Never a sale. */
  removable: boolean;
  /** The ZIP is kept. An order nobody paid for loses its file after a day. */
  hasFile: boolean;
  fileName: string;
  fileBytes: number;
  createdAt: string;
  paidAt: string | null;
}

export interface LogoOrderDetail extends LogoOrderRow {
  /** The logo as facts; null when the kept logo does not read as one any more. */
  logo: { name: string; slogan: string; colours: string[]; font: string } | null;
  /** What was bought, each thing at the price it was sold at; a business card's line says for how many names. */
  lines: Array<{ id: string; cents: number; names?: number }>;
  pageUrl: string;
  clientId: string | null;
}

export interface LogoOrdersPage {
  rows: LogoOrderRow[];
  total: number;
  page: number;
  pageSize: number;
}

export type LogoOrderFilter = "all" | "paid" | "pending";

export interface LogoOrdersQuery {
  q?: string;
  status?: LogoOrderFilter;
  page?: number;
  pageSize?: number;
}

function qs(params: LogoOrdersQuery): string {
  const p = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return p.length ? `?${p.join("&")}` : "";
}

export const getLogoOrders = (params: LogoOrdersQuery) => api.get<LogoOrdersPage>(`/admin/crm/logo-orders${qs(params)}`);
export const getLogoOrder = (id: string) => api.get<LogoOrderDetail>(`/admin/crm/logo-orders/${encodeURIComponent(id)}`);

/** Delete the ticked orders, as `removeCardOrders` does for card orders: with their ZIPs and their test invoices. */
export const removeLogoOrders = (ids: string[]) => api.post<OrdersRemoved>("/admin/crm/logo-orders/bulk-delete", { ids });

/** Where the logo of an order shows as a picture (the pack's own logo.png, read with the session cookie). */
export const logoOrderPictureUrl = (id: string): string => `/api/admin/crm/logo-orders/${encodeURIComponent(id)}/picture`;

/** Save the kept ZIP (a fetch with the session cookie and a blob, like the PDFs). */
export async function downloadLogoOrderFile(id: string, fileName: string): Promise<void> {
  const res = await fetch(`/api/admin/crm/logo-orders/${encodeURIComponent(id)}/file`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Paid means the money came. A logo order has its files before that, so "paid" is the whole of it. */
export function isPaidLogoOrder(row: Pick<LogoOrderRow, "status">): boolean {
  return row.status === "paid";
}

/** The name a saved file gets: the kept file's own name, or the order's code. */
export function logoOrderFileName(row: Pick<LogoOrderRow, "fileName" | "code">): string {
  return row.fileName || `${row.code}.zip`;
}

/** The word of each thing an order can hold, as a key of the CRM's catalogue. */
const LINE_WORDS: Record<string, string> = { logo: "sales.logoOrders.lineLogo", book: "sales.logoOrders.lineBook", social: "sales.logoOrders.lineSocial", card: "sales.logoOrders.lineCard" };

/** One thing an order holds, as the key of its word and what the word is filled with. */
export interface LogoOrderLineWord {
  key: string;
  vars?: { n: number };
}

/**
 * What an order holds, as the keys of its words, in the order's own sequence. A business
 * card of more than one name says how many. A thing this build has no word for is left
 * out: the amount still says what was paid.
 */
export function logoOrderLineWords(lines: ReadonlyArray<{ id: string; names?: number }> | null | undefined): LogoOrderLineWord[] {
  const out: LogoOrderLineWord[] = [];
  for (const line of lines ?? []) {
    const key = Object.hasOwn(LINE_WORDS, line.id) ? LINE_WORDS[line.id] : undefined;
    if (!key) continue;
    const names = line.id === "card" && typeof line.names === "number" && Number.isInteger(line.names) ? line.names : 1;
    out.push(names > 1 ? { key: "sales.logoOrders.lineCardNames", vars: { n: names } } : { key });
  }
  return out;
}

/** A colour of a logo as the desk may paint it: a six-digit hex and nothing else. */
export function swatchColour(value: string): string | null {
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : null;
}
